import { useTranslation } from "react-i18next";
import { Truck } from "lucide-react";
import { contentFor, formatPrice, type Locale } from "@nh/shared";
import type { CartEntry, CartSubtotal } from "@/features/cart/cart.pricing";
import { numberLocaleOf } from "@/features/shop/shop.catalog";

export interface CheckoutSummaryProps {
  entries: readonly CartEntry[];
  subtotal: CartSubtotal;
  locale: Locale;
  loading: boolean;
  /**
   * El cupón APLICADO y su descuento estimado (misma aritmética que el
   * servidor: `couponDiscountCents`). Con él, el resumen desglosa
   * subtotal − cupón = total.
   */
  coupon?: { code: string; discountCents: number } | null;
  /**
   * ── Renglones que NO salen del catálogo (Anahí, 29-ago) ────────────
   * La franquicia y un paquete no son productos: no tienen ficha ni
   * `translations`, y su precio ya viene resuelto. Cuando llegan, se
   * pintan en lugar de `entries` — el resto del resumen (envío, cupón,
   * total) es idéntico, que es justo por lo que no se duplicó esta
   * columna en otra pantalla.
   */
  customLines?: readonly { id: string; name: string; priceCents: number }[];
  /**
   * El envío (13-oct-2026): `charged` con lo que cuesta el servicio
   * elegido —entra al total—; `pending` mientras se cotiza; ausente
   * cuando el pedido no cobra envío y se coordina al confirmar el pago.
   */
  shipping?:
    | { status: "address" }
    | { status: "pending" }
    | { status: "charged"; cents: number; label: string }
    | null;
}

/**
 * El resumen del pedido, pegado a la derecha de los tres pasos — la
 * columna "Tu carrito" de producción. Los precios salen del catálogo
 * recién pedido, nunca de `localStorage`, por la regla de siempre del
 * carrito.
 */
export function CheckoutSummary({
  entries,
  subtotal,
  locale,
  loading,
  customLines,
  coupon,
  shipping,
}: CheckoutSummaryProps) {
  const { t } = useTranslation();
  const numberLocale = numberLocaleOf(locale);

  return (
    <aside className="h-fit rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[17px] font-semibold text-ds-ink">{t("Tu carrito")}</h2>
        <span className="rounded-ds-pill border border-ds-border px-3 py-1 text-[11px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {t("{{n}} artículos", { n: subtotal.countedItems })}
        </span>
      </div>

      <ul className="mt-4 grid gap-3">
        {customLines?.map((line) => (
          <li
            key={line.id}
            className="flex items-start justify-between gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-3"
          >
            <p className="min-w-0 text-[14px] font-medium text-ds-ink">{line.name}</p>
            <p className="shrink-0 text-[14px] font-semibold text-ds-ink">
              {formatPrice(line.priceCents, subtotal.currency, numberLocale)}
            </p>
          </li>
        ))}
        {!customLines && loading && entries.length === 0 && (
          <li className="text-[13px] text-ds-ink-3">{t("Cargando tu carrito…")}</li>
        )}
        {!customLines && entries.map((entry) => (
          <li
            key={entry.line.productId}
            className="flex items-start justify-between gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-3"
          >
            <div className="min-w-0">
              <p className="truncate text-[14px] font-medium text-ds-ink">
                {entry.product
                  ? contentFor(entry.product.translations, locale).name
                  : t("Producto no disponible")}
              </p>
              <p className="text-[12px] text-ds-ink-3">
                {t("Cantidad: {{n}}", { n: entry.line.quantity })}
              </p>
            </div>
            <p className="shrink-0 text-[14px] font-semibold text-ds-ink">
              {entry.price
                ? formatPrice(
                    entry.price.cents * entry.line.quantity,
                    entry.price.currency,
                    numberLocale,
                  )
                : "—"}
            </p>
          </li>
        ))}
      </ul>

      {!shipping && (
        <div className="mt-4 flex items-start gap-3 border-t border-ds-border pt-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ds-mist text-ds-ink-3">
            <Truck className="h-4 w-4" />
          </span>
          <p className="text-[12px] leading-relaxed text-ds-ink-3">
            {t("Los costos de envío y tiempos de entrega se coordinan al confirmar tu pago.")}
          </p>
        </div>
      )}

      <div className="mt-4 grid gap-1.5 border-t border-ds-border pt-4">
        {(coupon || shipping?.status === "charged") && (
          <div className="flex items-baseline justify-between text-[13px]">
            <span className="text-ds-ink-3">{t("Subtotal")}</span>
            <span className="text-ds-ink">
              {formatPrice(subtotal.cents, subtotal.currency, numberLocale)}
            </span>
          </div>
        )}
        {coupon && (
          <>
            <div className="flex items-baseline justify-between text-[13px]">
              <span className="text-ds-ink-3">
                {t("Cupón")} <span className="font-mono font-semibold">{coupon.code}</span>
              </span>
              <span className="font-medium text-ds-violet-pop">
                −{formatPrice(coupon.discountCents, subtotal.currency, numberLocale)}
              </span>
            </div>
          </>
        )}
        {shipping && (
          <div className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="text-ds-ink-3">
              {t("Envío")}
              {shipping.status === "charged" && (
                <span className="ml-1 text-ds-ink-3">· {shipping.label}</span>
              )}
            </span>
            <span className="shrink-0 text-ds-ink">
              {shipping.status === "charged"
                ? formatPrice(shipping.cents, subtotal.currency, numberLocale)
                : shipping.status === "pending"
                  ? t("Calculando…")
                  : t("Se calcula con tu dirección")}
            </span>
          </div>
        )}
        <div className="flex items-baseline justify-between">
          <span className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
            {t("Total")}
          </span>
          <span className="text-[22px] font-semibold text-ds-ink">
            {formatPrice(
              Math.max(0, subtotal.cents - (coupon?.discountCents ?? 0)) +
                (shipping?.status === "charged" ? shipping.cents : 0),
              subtotal.currency,
              numberLocale,
            )}
          </span>
        </div>
      </div>
    </aside>
  );
}
